import { onUnmounted, ref, type Ref } from "vue";
import { isTopmostOverlay } from "./overlayStack";

type UseModalEscapeOptions = {
  /** Корневой элемент содержимого модалки — по нему определяется, она ли сверху. */
  content: Ref<HTMLElement | null>;
  /** Закрытие модалки. */
  close: () => void;
  /**
   * Необязательный перехват. Вернуть true, если Escape уже обработан
   * (например, отменено инлайн-редактирование) и закрывать модалку не нужно.
   */
  intercept?: () => boolean;
};

/**
 * Закрытие модалки по Escape.
 *
 * BaseModal отдаёт в q-dialog persistent: true, из-за чего штатный ESC-dismiss
 * Quasar отключён и каждой модалке нужен свой обработчик.
 *
 * Слушатель висит на window в capture-фазе, чтобы отработать раньше
 * AppResizableDropdown (document, bubble), AppSelect (document, capture)
 * и AppInput (@keydown.esc на самом поле). Если сверху оказался другой оверлей,
 * обработчик молча возвращает управление — тот закроет себя сам.
 *
 * Вешается только на время открытия: модалки бывают вложенными (КА → счёт → КА)
 * и рендерятся сразу в нескольких местах.
 */
export function useModalEscape({ content, close, intercept }: UseModalEscapeOptions) {
  const isActive = ref(false);

  const handleEsc = (e: KeyboardEvent) => {
    if (e.key !== "Escape") {
      return;
    }
    if (!isActive.value) {
      return;
    }
    if (!isTopmostOverlay(content.value)) {
      return;
    }
    if (intercept?.()) {
      e.stopImmediatePropagation();
      return;
    }

    e.stopPropagation();
    close();
  };

  /** Вызывать при открытии модалки. */
  const activate = () => {
    isActive.value = true;
    window.addEventListener("keydown", handleEsc, true);
  };

  /** Вызывать из обработчика @on-close у BaseModal. */
  const deactivate = () => {
    isActive.value = false;
    window.removeEventListener("keydown", handleEsc, true);
  };

  onUnmounted(deactivate);

  return { activate, deactivate };
}
